const KEY_STATE = 'oidc.state';
const KEY_SILENT_STATE = 'oidc.silentState';
const KEY_VERIFIER = 'oidc.verifier';
const KEY_SILENT_VERIFIER = 'oidc.silentVerifier';
const KEY_RETURN_TO = 'oidc.returnTo';
const KEY_SIGNED_OUT = 'oidc.signedOut';
const KEY_FAILURES = 'oidc.handshakeFailures';
const KEY_FAILURE_REASON = 'oidc.handshakeFailureReason';

const read = (key: string): string | null => {
	try {
		return window.sessionStorage.getItem(key);
	} catch {
		return null;
	}
};

const write = (key: string, value: string): void => {
	try {
		window.sessionStorage.setItem(key, value);
	} catch {
		/* empty */
	}
};

const remove = (key: string): void => {
	try {
		window.sessionStorage.removeItem(key);
	} catch {
		/* empty */
	}
};

export const createRandomString = (): string => {
	const bytes = new Uint8Array(32);
	window.crypto.getRandomValues(bytes);
	return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

export type TConsumedHandshake = {
	valid: boolean;
	codeVerifier: string | null;
};

const consume = (
	stateKey: string,
	verifierKey: string,
	state: string | null,
): TConsumedHandshake => {
	const expected = read(stateKey);
	const codeVerifier = read(verifierKey);
	remove(stateKey);
	remove(verifierKey);
	return {
		valid: Boolean(state) && Boolean(expected) && state === expected,
		codeVerifier,
	};
};

export const oidcSession = {
	beginInteractive: (returnTo: string, codeVerifier: string): string => {
		const state = createRandomString();
		write(KEY_STATE, state);
		write(KEY_RETURN_TO, returnTo);
		write(KEY_VERIFIER, codeVerifier);
		return state;
	},

	beginSilent: (codeVerifier: string): string => {
		const state = createRandomString();
		write(KEY_SILENT_STATE, state);
		write(KEY_SILENT_VERIFIER, codeVerifier);
		return state;
	},

	consumeInteractive: (state: string | null): TConsumedHandshake =>
		consume(KEY_STATE, KEY_VERIFIER, state),

	consumeSilent: (state: string | null): TConsumedHandshake =>
		consume(KEY_SILENT_STATE, KEY_SILENT_VERIFIER, state),

	// Deliberately not consumed on read: a handshake that fails is going to be retried, and the
	// destination the user was heading for has to survive that retry.
	peekReturnTo: (): string | null => read(KEY_RETURN_TO),
	clearReturnTo: (): void => remove(KEY_RETURN_TO),

	markSignedOut: (): void => write(KEY_SIGNED_OUT, '1'),
	isSignedOut: (): boolean => read(KEY_SIGNED_OUT) === '1',
	clearSignedOut: (): void => remove(KEY_SIGNED_OUT),

	// A handshake can fail for reasons that do not survive a retry. Counting the failures is what
	// lets one hiccup be retried automatically while a systematic one still stops after a few
	// rounds instead of bouncing the browser between here and Keycloak forever.
	recordHandshakeFailure: (reason: string): number => {
		const attempts = Number(read(KEY_FAILURES) ?? '0') + 1;
		write(KEY_FAILURES, String(attempts));
		write(KEY_FAILURE_REASON, reason);
		return attempts;
	},
	handshakeFailureReason: (): string | null => read(KEY_FAILURE_REASON),
	clearHandshakeFailures: (): void => {
		remove(KEY_FAILURES);
		remove(KEY_FAILURE_REASON);
	},
};
